import { LONG_DASH, NOT_APPLICABLE } from '@dealstack/frontend/shared/other/react/specialSigns';
import { get, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

import { SecurityType } from '@dealstack/shared/lib/generated';
import { SecuritiesFormValues } from '@dealstack/frontend/shared/components/SecurityTablesModal/useSecuritiesTableController';
import { FormSingleSecurity } from '@dealstack/frontend/shared/components/SecurityTablesModal/useSecuritiesTableController.types';

export const useSecurityRowModalController = (fieldIndex: number, onRemove: () => void) => {
	const [showCheckSecurityNameAlert, setShowCheckSecurityNameAlert] = useState(false);

	const securitiesForm = useFormContext();

	const fieldKeys = useMemo(
		() => ({
			field: `${SecuritiesFormValues.securities}.${fieldIndex}` as `${SecuritiesFormValues.securities}.${number}`,
			name: `${SecuritiesFormValues.securities}.${fieldIndex}.name` as `${SecuritiesFormValues.securities}.${number}.name`,
			type: `${SecuritiesFormValues.securities}.${fieldIndex}.type` as `${SecuritiesFormValues.securities}.${number}.type`,
			votesPerUnit:
				`${SecuritiesFormValues.securities}.${fieldIndex}.votesPerUnit` as `${SecuritiesFormValues.securities}.${number}.votesPerUnit`,
			nominalValue:
				`${SecuritiesFormValues.securities}.${fieldIndex}.nominalValue` as `${SecuritiesFormValues.securities}.${number}.nominalValue`,
			fairMarketValuePerUnit:
				`${SecuritiesFormValues.securities}.${fieldIndex}.fairMarketValuePerUnit` as `${SecuritiesFormValues.securities}.${number}.fairMarketValuePerUnit`,
			isOnEdit:
				`${SecuritiesFormValues.securities}.${fieldIndex}.isOnEdit` as `${SecuritiesFormValues.securities}.${number}.isOnEdit`,
			isSubjectToVesting:
				`${SecuritiesFormValues.securities}.${fieldIndex}.isSubjectToVesting` as `${SecuritiesFormValues.securities}.${number}.isSubjectToVesting`,
		}),
		[fieldIndex]
	);

	const fieldData: FormSingleSecurity = securitiesForm.watch(fieldKeys.field);
	const handleBlur = (fieldKey: string) => {
		const fieldValue =
			fieldKey === fieldKeys.nominalValue
				? fieldData.nominalValue
				: fieldData.fairMarketValuePerUnit;
		const fieldValueToNumber = Number(fieldValue);

		if (!fieldValueToNumber) {
			return;
		}

		if (isFinite(fieldValueToNumber)) {
			securitiesForm.setValue(
				fieldKey,
				fieldValueToNumber.toFixed(fieldKeys.nominalValue ? 5 : 2)
			);
		}
	};

	const handleBlurVoterPerShare = () => {
		if (!fieldData.votesPerUnit) {
			return;
		}
		securitiesForm.setValue(fieldKeys.votesPerUnit, parseInt(String(fieldData.votesPerUnit)));
	};

	const { errors, touchedFields } = useFormState();
	const isValid =
		!get(errors, fieldKeys.name) &&
		!get(errors, fieldKeys.nominalValue) &&
		!get(errors, fieldKeys.votesPerUnit) &&
		!get(errors, fieldKeys.fairMarketValuePerUnit);

	const [fieldBackupData, setFieldBackupData] = useState<FormSingleSecurity>();

	useEffect(() => {
		if (!fieldData.isOnEdit) {
			return;
		}
		const isSecurityNameTouched = get(touchedFields, fieldKeys.name);
		if (isSecurityNameTouched) {
			void securitiesForm.trigger();
		}
	}, [
		fieldData.type,
		fieldData.name,
		fieldData.votesPerUnit,
		fieldData.nominalValue,
		fieldData.fairMarketValuePerUnit,
		fieldData.isSubjectToVesting,
	]);

	const startEdit = () => {
		securitiesForm.setValue(fieldKeys.isOnEdit, true);
	};

	const stopEdit = () => {
		const securitiesFields = get(securitiesForm.getValues(), SecuritiesFormValues.securities);
		const index = securitiesFields.findIndex((item) => item.id === fieldData.id);
		const field = securitiesFields[index];
		if (!field) {
			return;
		}
		securitiesForm.setValue(fieldKeys.isOnEdit, false);
	};

	const onSaveButtonClicked = () => {
		if (isSecurityNameChanged()) {
			return;
		}
		const isNameEmpty = fieldData.name.trim().length === 0;
		if (!isNameEmpty && isValid) {
			stopEdit();
			return;
		}
		void securitiesForm.trigger();
	};

	const isFirstElement = fieldIndex === 0;
	const onCancelButtonClicked = () => {
		/**
		 * 23.01.2023 MIP-226 D.slaby
		 * If id is present this means that this row is already saved in backend,
		 * if id is empty then it's local (temporary) element
		 */

		const isNoBackupName = isEmpty(fieldBackupData?.name?.trim());

		if (((fieldData.name.trim() === '' || !isValid) && isNoBackupName) || isNoBackupName) {
			onRemove();
			return;
		}

		if (!isNoBackupName) {
			securitiesForm.setValue(fieldKeys.field, fieldBackupData);
		}
		stopEdit();
	};

	const onEditButtonClicked = () => {
		setFieldBackupData({ ...fieldData });
		startEdit();
	};

	useEffect(() => {
		if (!fieldData.name && !fieldData.isOnEdit) {
			onEditButtonClicked();
		}
	}, []);

	const loansSecurityTypes = new Set([
		SecurityType.LOAN,
		SecurityType.OPTIONS,
		SecurityType.OTHER,
		SecurityType.WARRANT,
	]);

	const disableForLoansSecurityTypes = loansSecurityTypes.has(fieldData.type);

	const disableForVotes = fieldData.type === SecurityType.PARTNERSHIP_UNIT;

	const isSecurityNameChanged = useCallback(() => {
		if (!fieldBackupData || !fieldBackupData.hasTransactions) {
			return;
		}

		setShowCheckSecurityNameAlert(fieldBackupData.name !== fieldData?.name);
		return fieldBackupData?.name !== fieldData?.name;
	}, [fieldBackupData, fieldData]);

	const closeAlert = useCallback(() => {
		setShowCheckSecurityNameAlert(false);
	}, []);

	const onSubmit = useCallback(() => {
		stopEdit();
		closeAlert();
	}, [stopEdit, closeAlert]);

	const getVotesPerUnitValue = () => {
		if (disableForVotes || disableForLoansSecurityTypes) {
			return NOT_APPLICABLE;
		}
		if (fieldData.votesPerUnit) {
			return fieldData.votesPerUnit;
		}
		return LONG_DASH;
	};

	const getNominalValue = () => {
		if (disableForLoansSecurityTypes) {
			return NOT_APPLICABLE;
		}
		const value = Number(fieldData.nominalValue);
		if (value) {
			return value.toFixed(5);
		}
		return LONG_DASH;
	};

	const getFairMarketValuePerUnit = () => {
		if (disableForLoansSecurityTypes) {
			return NOT_APPLICABLE;
		}
		const value = Number(fieldData.fairMarketValuePerUnit);
		if (value) {
			return value.toFixed(2);
		}
		return LONG_DASH;
	};

	const votesPerUnitValue = getVotesPerUnitValue();
	const nominalValue = getNominalValue();
	const fairMarketValuePerUnit = getFairMarketValuePerUnit();

	return {
		fieldKeys,
		fieldData,
		isFirstElement,
		isValid,
		onSaveButtonClicked,
		onCancelButtonClicked,
		onEditButtonClicked,
		disableForLoansSecurityTypes,
		disableForVotes,
		handleBlur,
		closeAlert,
		showCheckSecurityNameAlert,
		stopEdit,
		onSubmit,
		handleBlurVoterPerShare,
		votesPerUnitValue,
		nominalValue,
		fairMarketValuePerUnit,
	};
};
